/**
 * The operator console.
 *
 * Every other screen in this application is inside one club. This one is
 * outside all of them, and it is the only screen in the product where
 * that is true — so it says so continuously rather than politely: a
 * different top bar, the platform's name rather than a club's, and the
 * count of clubs where a club screen would put the club's own name.
 *
 * That is not decoration. The dangerous mistake here is not pressing the
 * wrong button, it is forgetting which hat you are wearing while you
 * press the right one.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../lib/api.ts';
import { useMutation } from '../lib/useApi.ts';
import { OperatorProvider, platformApi, useOperator } from '../lib/operator.tsx';
import { BetterClubLockup } from '../components/brand.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

// =====================================================================
// Shapes
// =====================================================================

interface ClubRow {
  id: string;
  slug: string;
  legal_name: string;
  brand_name: string;
  status: 'active' | 'suspended' | 'closed';
  is_demo: boolean;
  copied_from: string | null;
  opened_on: string | null;
  created_at: string;
  member_count: number;
  login_count: number;
  sample_rows: number;
  own_records: number;
  trial_balance: number;
  last_activity_at: string | null;
}

interface ClubDetail {
  id: string;
  slug: string;
  legal_name: string;
  brand_name: string;
  status: 'active' | 'suspended' | 'closed';
  status_reason: string | null;
  status_changed_at: string | null;
  is_demo: boolean;
  copied_from: string | null;
  country_code: string;
  base_currency: string;
  timezone: string;
  created_at: string;
  opened_on: string | null;
  logins: {
    email: string;
    display_name: string;
    role: string;
    status: string;
    last_login_at: string | null;
  }[];
  sample_modules: { module: string; rows: number; loaded_at: string }[];
  counts: { members: number; vendors: number; entries: number; accounts: number };
  trial_balance: number;
  recent: { at: string; action: string; operator: string | null; detail: unknown }[];
}

// =====================================================================
// Small helpers
// =====================================================================

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function day(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function statusTone(s: string): 'good' | 'warning' | 'serious' | 'neutral' {
  if (s === 'active') return 'good';
  if (s === 'suspended') return 'warning';
  if (s === 'closed') return 'serious';
  return 'neutral';
}

/** A confirmation that costs something to give. Same shape as Settings uses. */
function Confirm({
  word,
  children,
  busy,
  onConfirm,
  onCancel,
}: {
  word: string;
  children: React.ReactNode;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  return (
    <div className="confirm">
      <div className="confirm__body">{children}</div>
      <label className="confirm__field">
        <span>
          Type <strong>{word}</strong> to confirm
        </span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus spellCheck={false} />
      </label>
      <div className="confirm__actions">
        <button className="danger" disabled={busy || typed.trim() !== word} onClick={onConfirm}>
          {busy ? 'Working…' : 'Yes, do it'}
        </button>
        <button className="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The platform data hook. Not `useApi` — that one carries the club token. */
function usePlatform<T>(path: string | null): {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useMemo(() => {
    if (!path) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    platformApi<T>(path)
      .then((r) => !cancelled && setData(r))
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e : new ApiError(0, 'network', String(e))))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [path, nonce]);

  return { data, error, loading, reload };
}

// =====================================================================
// Signing in
// =====================================================================

function OperatorLogin() {
  const { signIn } = useOperator();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const go = useMutation(async () => {
    await signIn(email.trim(), password);
  });

  return (
    <div className="operator-login">
      <div className="login-card">
        {/* The same lockup, at the same size, in the same place as the club's
            own sign-in card — this is the same moment for a different
            principal, and it should read that way. */}
        <div className="login-card__mark">
          <BetterClubLockup height={40} />
        </div>
        <div className="login-card__label">THE PLATFORM, NOT A CLUB</div>
        <h1>Operator Console</h1>
        <p className="muted small">
          This is not a club login. It signs you in to the platform itself, which is a
          different account from any club's.
        </p>

        <ErrorBanner error={go.error} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void go.run();
          }}
        >
          <div className="form-rows">
            <label>
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
              />
            </label>
            <label>
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
          </div>
          <div className="actions" style={{ marginTop: 14 }}>
            <button type="submit" disabled={go.busy || !email.trim() || !password}>
              {go.busy ? 'Signing in…' : 'Sign in'}
            </button>
            <Link className="muted small" to="/">
              Back to the club
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// The estate
// =====================================================================

function ClubList() {
  const { data, error, loading, reload } = usePlatform<{ clubs: ClubRow[] }>('/v1/platform/clubs');
  const clubs = data?.clubs ?? [];

  const real = clubs.filter((c) => !c.is_demo);
  const demos = clubs.filter((c) => c.is_demo);
  const unbalanced = clubs.filter((c) => Number(c.trial_balance) !== 0);

  if (loading && !data) return <Loading rows={6} />;

  return (
    <>
      <h1>Clubs</h1>
      <ErrorBanner error={error} />

      <div className="tiles">
        <Tile label="Clubs" value={real.length} note={`${demos.length} demo`} />
        <Tile
          label="Suspended or Closed"
          value={clubs.filter((c) => c.status !== 'active').length}
          tone={clubs.some((c) => c.status !== 'active') ? 'warning' : undefined}
          note={clubs.some((c) => c.status !== 'active') ? 'Their logins are being refused' : undefined}
        />
        <Tile label="Members" value={real.reduce((n, c) => n + Number(c.member_count), 0).toLocaleString()} />
        <Tile
          label="Books That Balance"
          value={`${clubs.length - unbalanced.length} of ${clubs.length}`}
          tone={unbalanced.length ? 'critical' : undefined}
          note={unbalanced.length ? unbalanced.map((c) => c.slug).join(', ') : undefined}
        />
      </div>

      <Card
        title="Every Club"
        actions={
          <div className="actions">
            <button className="secondary small" onClick={reload}>
              Refresh
            </button>
            <Link className="button-link" to="/operator/clubs/new">
              Create a club
            </Link>
          </div>
        }
        flush
      >
        <table className="data">
          <thead>
            <tr>
              <th>Club</th>
              <th>Status</th>
              <th className="num">Members</th>
              <th className="num">Logins</th>
              <th className="num">Sample</th>
              <th>Books</th>
              <th>Last posting</th>
            </tr>
          </thead>
          <tbody>
            {clubs.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/operator/clubs/${c.id}`}>
                    <strong>{c.legal_name}</strong>
                  </Link>
                  <div className="muted small">
                    {c.slug}
                    {c.is_demo && ' · demo'}
                    {c.copied_from && ` · copied from ${c.copied_from}`}
                  </div>
                </td>
                <td>
                  <Status tone={statusTone(c.status)}>{c.status}</Status>
                </td>
                <td className="num">{Number(c.member_count).toLocaleString()}</td>
                <td className="num">{Number(c.login_count).toLocaleString()}</td>
                <td className="num">
                  {Number(c.sample_rows) ? Number(c.sample_rows).toLocaleString() : '—'}
                </td>
                <td>
                  {Number(c.trial_balance) === 0 ? (
                    <span className="muted small">balanced</span>
                  ) : (
                    <Status tone="critical">out by {Number(c.trial_balance)}</Status>
                  )}
                </td>
                <td className="muted small">{day(c.last_activity_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {clubs.length === 0 && <Empty>No clubs yet.</Empty>}
    </>
  );
}

// =====================================================================
// One club
// =====================================================================

function ClubDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { openClub } = useOperator();

  const { data, error, loading, reload } = usePlatform<ClubDetail>(`/v1/platform/clubs/${id}`);
  const [note, setNote] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [openReason, setOpenReason] = useState('');

  const setStatus = useMutation(async (status: string) => {
    await platformApi(`/v1/platform/clubs/${id}/status`, {
      method: 'POST',
      body: { status, reason: reason.trim() || undefined },
    });
    setReason('');
    setNote(
      status === 'active'
        ? 'Reactivated. Its logins work again.'
        : `Now ${status}. Anyone signing in is told: "${reason.trim()}"`,
    );
    reload();
  });

  const open = useMutation(async () => {
    const name = await openClub(id, openReason.trim());
    setNote(`Opening ${name}…`);
    navigate('/');
  });

  const remove = useMutation(async (slug: string) => {
    const r = await platformApi<{ rows_removed: number }>(`/v1/platform/clubs/${id}`, {
      method: 'DELETE',
      body: { confirm: slug },
    });
    navigate('/operator', { replace: true });
    return r;
  });

  if (loading && !data) return <Loading rows={8} />;
  if (!data) {
    return (
      <>
        <h1>Club</h1>
        <ErrorBanner error={error} />
      </>
    );
  }

  const c = data;

  return (
    <>
      <div className="actions" style={{ marginBottom: 6 }}>
        <Link className="muted small" to="/operator">
          ← Every club
        </Link>
      </div>
      <h1>{c.legal_name}</h1>
      <p className="muted">
        {c.slug} · {c.brand_name} · {c.country_code} · {c.base_currency} · {c.timezone}
        {c.is_demo && ' · a demo club'}
        {c.copied_from && ` · configured like ${c.copied_from}`}
      </p>

      <ErrorBanner error={error ?? setStatus.error ?? open.error ?? remove.error} />
      {note && <div className="notice">{note}</div>}

      <div className="tiles">
        <Tile
          label="Status"
          value={c.status}
          tone={statusTone(c.status)}
          note={c.status === 'active' ? `Open since ${day(c.opened_on)}` : c.status_reason ?? undefined}
        />
        <Tile label="Members" value={c.counts.members.toLocaleString()} />
        <Tile label="Journal Entries" value={c.counts.entries.toLocaleString()} />
        <Tile
          label="Books"
          value={Number(c.trial_balance) === 0 ? 'Balanced' : 'Out'}
          tone={Number(c.trial_balance) === 0 ? 'good' : 'critical'}
          note={Number(c.trial_balance) === 0 ? undefined : `by ${c.trial_balance}`}
        />
      </div>

      <Card title="Go In">
        <p className="muted small">
          Signs you into this club as support, for fifteen minutes, with your own name
          recorded inside the token. Everything you do there is this club's record, marked as
          yours. A suspended club can still be opened this way — that is rather the point of
          being able to look at one.
        </p>
        <div className="form-rows">
          <label>
            <span>Why (recorded against the club)</span>
            <input
              value={openReason}
              onChange={(e) => setOpenReason(e.target.value)}
              placeholder="Checking a reported problem with the tee sheet"
            />
          </label>
        </div>
        <div className="actions">
          <button onClick={() => open.run()} disabled={open.busy || !openReason.trim()}>
            {open.busy ? 'Opening…' : `Open ${c.brand_name}`}
          </button>
        </div>
      </Card>

      <Card title="Status">
        {c.status !== 'active' && c.status_reason && (
          <p className="muted small">
            Since {when(c.status_changed_at)}, anyone signing in is told:{' '}
            <strong>{c.status_reason}</strong>
          </p>
        )}

        {c.status === 'closed' ? (
          <p className="muted small">
            This club is closed. Its records are kept and nothing here reopens it — that is a
            decision worth having a conversation about rather than a dropdown.
          </p>
        ) : (
          <>
            <div className="form-rows">
              <label>
                <span>Reason, if you are taking it offline</span>
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Trial ended on 30 September. Call the office to reactivate."
                />
              </label>
            </div>
            <div className="actions">
              {c.status === 'active' ? (
                <>
                  <button
                    className="secondary"
                    onClick={() => setStatus.run('suspended')}
                    disabled={setStatus.busy || !reason.trim()}
                  >
                    Suspend
                  </button>
                  <button
                    className="danger"
                    onClick={() => setStatus.run('closed')}
                    disabled={setStatus.busy || !reason.trim()}
                  >
                    Close permanently
                  </button>
                </>
              ) : (
                <button onClick={() => setStatus.run('active')} disabled={setStatus.busy}>
                  Reactivate
                </button>
              )}
            </div>
            <p className="muted small">
              A suspended club refuses its own logins with the reason above, and nothing else.
              Write it for whoever will read it on a login screen.
            </p>
          </>
        )}
      </Card>

      <Card title="Logins" flush>
        {c.logins.length === 0 ? (
          <div className="card__body">
            <Empty>No logins. Nobody at this club can sign in.</Empty>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Who</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last signed in</th>
              </tr>
            </thead>
            <tbody>
              {c.logins.map((u) => (
                <tr key={u.email}>
                  <td>
                    <strong>{u.display_name}</strong>
                    <div className="muted small">{u.email}</div>
                  </td>
                  <td>{u.role.replace('_', ' ')}</td>
                  <td>
                    <Status tone={u.status === 'active' ? 'good' : 'neutral'}>{u.status}</Status>
                  </td>
                  <td className="muted small">{when(u.last_login_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {c.sample_modules.length > 0 && (
        <Card title="Sample Data">
          <p className="muted small">
            {c.sample_modules.map((m) => `${m.module} (${m.rows.toLocaleString()})`).join(' · ')}
          </p>
        </Card>
      )}

      <Card title="What Operators Have Done Here" flush>
        {c.recent.length === 0 ? (
          <div className="card__body">
            <Empty>Nothing yet.</Empty>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th>Who</th>
              </tr>
            </thead>
            <tbody>
              {c.recent.map((r, i) => (
                <tr key={`${r.at}-${i}`}>
                  <td className="muted small">{when(r.at)}</td>
                  <td>{r.action}</td>
                  <td className="muted small">{r.operator ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {c.is_demo && (
        <Card title="Delete This Club">
          <p className="muted small">
            A demo club and everything in it. This is the only kind of club that can be
            deleted at all — a real club is closed instead, because its records have to
            outlive it.
          </p>
          {deleting ? (
            <Confirm
              word={c.slug}
              busy={remove.busy}
              onConfirm={() => remove.run(c.slug)}
              onCancel={() => setDeleting(false)}
            >
              <p>
                {c.legal_name}, its {c.counts.members.toLocaleString()} members, its{' '}
                {c.counts.entries.toLocaleString()} journal entries, its logins and its whole
                configuration. There is no undo.
              </p>
            </Confirm>
          ) : (
            <div className="actions">
              <button className="danger" onClick={() => setDeleting(true)}>
                Delete {c.slug}
              </button>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

// =====================================================================
// Creating one
// =====================================================================

function NewClubPage() {
  const navigate = useNavigate();
  const { data } = usePlatform<{ clubs: ClubRow[] }>('/v1/platform/clubs');

  const [slug, setSlug] = useState('');
  const [legalName, setLegalName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [like, setLike] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [loadSample, setLoadSample] = useState(false);
  const [made, setMade] = useState<{ club: ClubDetail; password: string | null } | null>(null);

  const templates = (data?.clubs ?? []).filter((c) => !c.is_demo);

  const create = useMutation(async () => {
    const r = await platformApi<{
      club: ClubDetail;
      first_login: { email: string; password: string | null };
    }>('/v1/platform/clubs', {
      method: 'POST',
      body: {
        slug: slug.trim(),
        legal_name: legalName.trim(),
        brand_name: brandName.trim() || undefined,
        like: like || undefined,
        admin_email: adminEmail.trim(),
        admin_name: adminName.trim(),
        is_demo: isDemo,
        load_sample: loadSample,
      },
    });
    setMade({ club: r.club, password: r.first_login?.password ?? null });
    return r;
  });

  if (made) {
    return (
      <>
        <h1>{made.club.legal_name}</h1>
        <div className="notice">
          Built. {made.club.counts.accounts.toLocaleString()} accounts,{' '}
          {made.club.counts.members.toLocaleString()} members, and a login that works.
        </div>

        {made.password && (
          <Card title="The First Password">
            <p className="muted small">
              Shown once and never again — it is not stored anywhere it can be read back. The
              account is flagged to change it at first sign-in, so the credential you carry
              from here to the club does not survive arrival.
            </p>
            <pre className="secret">{made.password}</pre>
            <p className="muted small">for {adminEmail.trim()}</p>
          </Card>
        )}

        <div className="actions">
          <button onClick={() => navigate(`/operator/clubs/${made.club.id}`)}>
            Open its record
          </button>
          <button className="secondary" onClick={() => navigate('/operator')}>
            Every club
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="actions" style={{ marginBottom: 6 }}>
        <Link className="muted small" to="/operator">
          ← Every club
        </Link>
      </div>
      <h1>Create a Club</h1>
      <p className="muted">
        A new tenant, configured like one that already works — its departments, chart of
        accounts, tax codes, course and rate card — with its first administrator login. Both
        halves, because a club nobody can sign into is not a club.
      </p>

      <ErrorBanner error={create.error} />

      <Card title="The Club">
        <div className="form-rows">
          <label>
            <span>Address</span>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="oakbourne"
              spellCheck={false}
            />
          </label>
          <label>
            <span>Legal name</span>
            <input
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="Oakbourne Golf &amp; Country Club"
            />
          </label>
          <label>
            <span>What members call it</span>
            <input
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="The Oaks"
            />
          </label>
          <label>
            <span>Configured like</span>
            <select value={like} onChange={(e) => setLike(e.target.value)}>
              <option value="">The oldest club on the platform</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.legal_name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      <Card title="Its First Login">
        <div className="form-rows">
          <label>
            <span>Email</span>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="manager@oakbourne.com"
            />
          </label>
          <label>
            <span>Name</span>
            <input
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="Club Administrator"
            />
          </label>
        </div>
        <p className="muted small">
          A password is generated and shown to you once. The account is a club administrator
          and is flagged to change it at first sign-in.
        </p>
      </Card>

      <Card title="What Goes in It">
        <div className="choices">
          <label className="inline-choice">
            <input type="checkbox" checked={isDemo} onChange={(e) => setIsDemo(e.target.checked)} />
            <span>
              A demo club
              <em>
                Never billed, never counted, and the only kind of club that can be deleted
                afterwards.
              </em>
            </span>
          </label>
          <label className="inline-choice">
            <input
              type="checkbox"
              checked={loadSample}
              onChange={(e) => setLoadSample(e.target.checked)}
            />
            <span>
              Fill it with sample data
              <em>
                Seven modules, about seven and a half thousand rows, posted through the real
                engines. Takes a few seconds. Removable from inside the club afterwards.
              </em>
            </span>
          </label>
        </div>
      </Card>

      <div className="actions">
        <button
          onClick={() => create.run()}
          disabled={
            create.busy ||
            !slug.trim() ||
            !legalName.trim() ||
            !adminEmail.trim() ||
            !adminName.trim()
          }
        >
          {create.busy ? 'Building…' : 'Create the club'}
        </button>
      </div>
    </>
  );
}

// =====================================================================
// The shell
// =====================================================================

function OperatorShell() {
  const { operator, signOut } = useOperator();

  return (
    <div className="app app--operator">
      <div className="main">
        <header className="topbar topbar--operator">
          <BetterClubLockup height={20} />
          <strong className="topbar__name">Operator Console</strong>
          <span className="muted small">the platform, not a club</span>
          <div className="topbar__spacer" />
          <span className="muted small">{operator?.email}</span>
          <button className="secondary small" onClick={signOut}>
            Sign out
          </button>
        </header>

        <main className="content">
          <Routes>
            <Route path="/" element={<ClubList />} />
            <Route path="/clubs/new" element={<NewClubPage />} />
            <Route path="/clubs/:id" element={<ClubDetailPage />} />
            <Route path="*" element={<Navigate to="/operator" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

/**
 * The wall between a first sign-in and the console.
 *
 * `must_change_password` was being set correctly, returned correctly, and
 * read by nobody — an operator created with a generated password arrived at
 * the club list and was never asked for a new one. The credential that gets
 * typed into a chat window, pasted into a ticket, or read down a telephone is
 * exactly the one the flag exists to retire, and it was outliving first use
 * on the single account that can delete a club.
 *
 * Rendered instead of the shell rather than over it. There is no version of
 * this a person should be able to dismiss, and a modal is a thing people
 * learn to dismiss.
 */
function MustChangePassword() {
  const { operator, refresh, signOut } = useOperator();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = again.length > 0 && next !== again;
  const reused = next.length > 0 && next === current;

  const go = useMutation(async () => {
    await platformApi('/v1/platform/auth/password', {
      method: 'POST',
      body: { current_password: current, new_password: next },
    });
    // The flag lives on the server. Re-reading it is what takes this screen
    // down, so a failed write cannot leave the console open behind it.
    await refresh();
  });

  return (
    <div className="operator-login">
      <div className="login-card">
        <div className="login-card__mark">
          <BetterClubLockup height={40} />
        </div>
        <div className="login-card__label">THE PLATFORM, NOT A CLUB</div>
        <h1>Choose a New Password</h1>
        <p className="muted small">
          The password you signed in with was issued to you, which means it has been
          written down somewhere at least once. This account can create, suspend and
          delete clubs, so it does not keep a password anybody else has seen.
        </p>

        <ErrorBanner error={go.error} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void go.run();
          }}
        >
          <div className="form-rows">
            <label>
              <span>Email</span>
              <input type="email" value={operator?.email ?? ''} readOnly autoComplete="username" />
            </label>
            <label>
              <span>The password you just used</span>
              <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
            </label>
            <label>
              <span>New password</span>
              <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
              />
              <span className="muted small">
                {tooShort
                  ? `At least 12 characters — ${12 - next.length} to go.`
                  : reused
                    ? 'That is the password you are replacing.'
                    : 'At least 12 characters.'}
              </span>
            </label>
            <label>
              <span>New password again</span>
              <input
                type="password"
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                autoComplete="new-password"
              />
              {mismatch ? <span className="muted small">These two do not match.</span> : null}
            </label>
          </div>
          <div className="actions" style={{ marginTop: 14 }}>
            <button
              type="submit"
              disabled={
                go.busy || !current || next.length < 12 || next !== again || reused
              }
            >
              {go.busy ? 'Saving…' : 'Save and continue'}
            </button>
            <button type="button" className="secondary small" onClick={signOut}>
              Sign out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OperatorGate() {
  const { loading, operator } = useOperator();
  if (loading) {
    return (
      <div style={{ padding: 40, maxWidth: 480 }}>
        <Loading rows={4} />
      </div>
    );
  }
  if (!operator) return <OperatorLogin />;
  if (operator.must_change_password) return <MustChangePassword />;
  return <OperatorShell />;
}

export function OperatorApp() {
  return (
    <OperatorProvider>
      <OperatorGate />
    </OperatorProvider>
  );
}
